import { fromJS, List, Map } from "immutable";
import { GenericActions } from "../../redux/generic-actions";
import { GenericFormActions } from "./actions";

export const defaultInitialState = fromJS({
    formUI: {
        isVisible: true,
        isNewForm: false,
        isEditForm: false,
    },
    formData: {},
    formReadOnly: {},
    formValidation: {
        requiredFields: [],
        errors: {},
        mutationMessage: {},
    },
});

export function commonFormReducerFactory(rootProp, initialState) {
    return function commonFormReducer(state = initialState, action) {
        state = fieldValueUpdated(state, action, rootProp, initialState);
        state = detailLoaded(state, action, rootProp, initialState);
        state = formOpened(state, action, rootProp, initialState);
        state = formClosed(state, action, rootProp, initialState);
        state = blankGroupAdded(state, action, rootProp, initialState);
        state = groupRemoved(state, action, rootProp, initialState);
        state = fieldRemovedFromForm(state, action, rootProp, initialState);
        state = stateReset(state, action, rootProp, initialState);
        state = serverErrorMessageAdded(state, action, rootProp, initialState);
        state = clientErrorMessageAdded(state, action, rootProp, initialState);
        state = saveSuccess(state, action, rootProp, initialState);
        state = componentAdded(state, action, rootProp, initialState);
        state = componentRemoved(state, action, rootProp, initialState);
        return state;
    };
}

function createListIfNeeded(state, action) {
    const indexedListPath = action.fieldProp.match(/\:\:\d+\:\:/);
    let listRootPath = action.fieldProp;
    if (!!indexedListPath) {
        listRootPath = action.fieldProp.slice(0, indexedListPath.index);
    }
    listRootPath = listRootPath.split("::");
    if (!!action.options &&
        !!action.options.groupContext &&
        !state.getIn([action.stateProp, "formData", ...listRootPath])) {
        state = state.setIn([action.stateProp, "formData", ...listRootPath], List());
    }
    return state;
}

function fieldValueUpdated(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.FIELD_VALUE_UPDATED}`:
            if (action.payload === undefined) {
                return state;
            }
            const fieldPropPath = action.fieldProp.split("::");
            state = createListIfNeeded(state, action);
            if (!!action.options && !!action.options.isList) {
                let values = state.getIn([action.stateProp, "formData", ...fieldPropPath]) || List();
                const startIndex = action.options.startIndex || 0;
                const numValuesToRemove = values.count() - startIndex;
                values = values.splice(startIndex, numValuesToRemove, ...action.payload);
                state = state.setIn([action.stateProp, "formData", ...fieldPropPath], values);
            } else {
                state = state.setIn([action.stateProp, "formData", ...fieldPropPath], action.payload);
            }

            return state;

        default:
            return state;
    }
}

function detailLoaded(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericActions.DETAIL_LOADED}`:
            const payload = fromJS(action.payload);
            const options = action.options;
            if (!!options.partial && !!options.fieldProp) {
                const fieldPropPath = action.options.fieldProp.split("::");
                let partialFormData = state.getIn([action.stateProp, "formData", ...fieldPropPath]);
                if (partialFormData instanceof List) {
                    partialFormData = (partialFormData as any).splice(
                        options.partialInsertIndex,
                        payload.count(),
                        ...payload.toArray(),
                    );
                } else {
                    partialFormData = payload;
                }
                state = state.setIn([action.stateProp, "formData", ...fieldPropPath], partialFormData);
            } else {
                state = state.mergeIn([action.stateProp, "formData"], payload);
            }
            return state;

        default:
            return state;
    }
}
function formOpened(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.FORM_OPENED}`:
            state = state.setIn([action.stateProp, "formUI", "isVisible"], true);
            if (action.payload.detailId) {
                state = state.setIn([action.stateProp, "formReadOnly", "detailId"], action.payload.detailId);
            }
            return state;

        default:
            return state;
    }
}
function formClosed(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.FORM_CLOSED}`:
            return state.setIn([action.stateProp, "formUI", "isVisible"], false);

        default:
            return state;
    }
}
function blankGroupAdded(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.BLANK_GROUP_ADDED}`:
            const fieldPropPath = action.fieldProp.split("::");
            const group = state.getIn([action.stateProp, "formData", ...fieldPropPath]) || List();
            const groupCount = group.count();
            state = createListIfNeeded(state, action);
            return state.setIn([action.stateProp, "formData", ...fieldPropPath, groupCount], Map());

        default:
            return state;
    }
}
function groupRemoved(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.GROUP_REMOVED}`:
            const fieldPropPath = action.fieldProp.split("::");
            state = state.deleteIn([action.stateProp, "formData", ...fieldPropPath, action.payload.toString()]);
            return state;

        default:
            return state;
    }
}
function fieldRemovedFromForm(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.FIELD_REMOVED_FROM_FORM}`:
            const fieldPropPath = action.fieldProp.split("::");
            if (state.getIn([action.stateProp, "formData", ...fieldPropPath])) {
                state = state.deleteIn([action.stateProp, "formData", ...fieldPropPath]);
            }
            return state;

        default:
            return state;
    }
}

function stateReset(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericActions.STATE_RESET}`:
            if (action.onlyResetFormData) {
                state = state.setIn([action.stateProp, "formData"], action.payload.formData);
            } else {
                state = state.set(action.stateProp, action.payload);
            }
            return state;

        default:
            return state;
    }
}

function serverErrorMessageAdded(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.SERVER_ERROR_MESSAGE_ADDED}`:
            return state.mergeIn([action.stateProp, "formValidation", "mutationMessage"], {
                message: action.payload,
                isSuccess: false,
            });

        default:
            return state;
    }
}
function clientErrorMessageAdded(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.CLIENT_ERROR_MESSAGE_ADDED}`:
            if (!!action.payload.errorMessage) {
                state = state.setIn([action.stateProp, "formValidation", "errors", action.fieldProp], action.payload);
            } else {
                state = state.deleteIn([action.stateProp, "formValidation", "errors", action.fieldProp]);
            }
            return state;

        default:
            return state;
    }
}
function saveSuccess(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericFormActions.SAVE_SUCCESS}`:
            return state.mergeIn([action.stateProp, "formValidation", "mutationMessage"], {
                message: action.payload,
                isSuccess: true,
            });

        default:
            return state;
    }
}
function componentAdded(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericActions.COMPONENT_ADDED}`:
            const detailId = state.getIn([action.stateProp, "formReadOnly", "detailId"]);
            state = state.set(action.stateProp, fromJS(action.payload));
            state = state.setIn([action.stateProp, "formReadOnly", "detailId"], detailId);
            return state;

        default:
            return state;
    }
}
function componentRemoved(state, action, rootProp, initialState) {
    switch (action.type) {
        case `${rootProp}::${GenericActions.COMPONENT_REMOVED}`:
            return state.deleteIn([action.stateProp]);

        default:
            return state;
    }
}
